/**
 * Operator Readiness Standards
 * Physical benchmarks for tactical athletes based on military/SOF standards
 */

export interface PhysicalStandards {
  // Strength (relative to bodyweight)
  deadlift: number;      // Multiple of bodyweight (e.g., 1.5x = elite)
  squat: number;         // Multiple of bodyweight
  bench: number;         // Multiple of bodyweight
  
  // Endurance (time in seconds)
  ruck6Mile: number;     // 6-mile ruck with 45lbs
  run1_5Mile: number;    // 1.5-mile run
  
  // Bodyweight
  pullUps: number;       // Max reps
  pushUps: number;       // Max reps in 2 min
  dips: number;          // Max reps (strict dips)
  
  label: string;
  description: string;
}

/**
 * Standard benchmarks (based on military/SOF requirements)
 */
export const OPERATOR_STANDARDS: Record<string, PhysicalStandards> = {
  elite: {
    deadlift: 2.0,        // 2x bodyweight
    squat: 1.75,          // 1.75x bodyweight
    bench: 1.5,           // 1.5x bodyweight
    ruck6Mile: 5400,      // 90 minutes or less
    run1_5Mile: 600,      // 10:00 or less
    pullUps: 20,          // 20+ pull-ups
    pushUps: 80,          // 80+ push-ups in 2 min
    dips: 30,             // 30+ strict dips
    label: 'ELITE',
    description: 'Exceeds SOF selection standards'
  },
  advanced: {
    deadlift: 1.75,       // 1.75x bodyweight
    squat: 1.5,           // 1.5x bodyweight
    bench: 1.25,          // 1.25x bodyweight
    ruck6Mile: 6000,      // 100 minutes
    run1_5Mile: 660,      // 11:00
    pullUps: 15,          // 15 pull-ups
    pushUps: 65,          // 65 push-ups
    dips: 25,             // 25 dips
    label: 'ADVANCED',
    description: 'Meets SOF selection standards'
  },
  proficient: {
    deadlift: 1.5,        // 1.5x bodyweight
    squat: 1.25,          // 1.25x bodyweight
    bench: 1.0,           // 1x bodyweight
    ruck6Mile: 6600,      // 110 minutes
    run1_5Mile: 720,      // 12:00
    pullUps: 12,          // 12 pull-ups
    pushUps: 50,          // 50 push-ups
    dips: 20,             // 20 dips
    label: 'PROFICIENT',
    description: 'Meets military fitness standards'
  },
  developing: {
    deadlift: 1.25,       // 1.25x bodyweight
    squat: 1.0,           // 1x bodyweight
    bench: 0.75,          // 0.75x bodyweight
    ruck6Mile: 7200,      // 120 minutes
    run1_5Mile: 840,      // 14:00
    pullUps: 8,           // 8 pull-ups
    pushUps: 35,          // 35 push-ups
    dips: 15,             // 15 dips
    label: 'DEVELOPING',
    description: 'Building toward standards'
  },
  beginner: {
    deadlift: 1.0,        // 1x bodyweight
    squat: 0.75,          // 0.75x bodyweight
    bench: 0.5,           // 0.5x bodyweight
    ruck6Mile: 9000,      // 150 minutes
    run1_5Mile: 1020,     // 17:00
    pullUps: 5,           // 5 pull-ups
    pushUps: 20,          // 20 push-ups
    dips: 10,             // 10 dips
    label: 'BEGINNER',
    description: 'Starting tactical fitness journey'
  }
};

/**
 * Detraining decay multipliers. Per the literature (Bosquet 2013 meta;
 * Mujika & Padilla 2000/2001), strength and endurance capacities decay
 * during inactivity at meaningfully different rates:
 *
 *   - 1RM strength: ~5% loss at 4 wk, ~10-15% at 8 wk, ~25% at 12 wk
 *   - VO2max / endurance: ~7% at 4 wk, ~20% at 8 wk, ~30% at 12 wk
 *
 * Both decays start at day 14 — earlier gaps are normal training rhythm,
 * not detraining (Bosquet's meta finds negligible loss ≤2 weeks).
 * Both have floors (40% strength, 25% endurance) — a deeply detrained
 * operator still retains motor patterns + base capacity per Seaborne 2018
 * muscle-memory work.
 */
export function strengthDecayFactor(daysSinceLastSession: number): number {
  if (daysSinceLastSession < 14) return 1.0;
  const overage = daysSinceLastSession - 14;
  return Math.max(0.4, 1 - 0.003 * overage);
  // 14d: 1.0  · 30d: 0.952 · 60d: 0.862 · 90d: 0.772 · 167d: 0.5 · 214d: 0.4 floor
}

export function enduranceDecayFactor(daysSinceLastSession: number): number {
  if (daysSinceLastSession < 14) return 1.0;
  const overage = daysSinceLastSession - 14;
  return Math.max(0.25, 1 - 0.006 * overage);
  // 14d: 1.0  · 30d: 0.904 · 60d: 0.724 · 90d: 0.544 · 139d: 0.25 floor
}

/**
 * Calculate battle readiness score (0-100)
 */
export function calculateBattleReadiness(params: {
  // Current performance
  bodyweight: number;
  deadlift1RM: number;
  squat1RM: number;
  bench1RM: number;
  ruck6MileTime: number | null;      // seconds
  run1_5MileTime: number | null;     // seconds
  /**
   * Recent conditioning training load (for sessions like HIIT, intervals, steady cardio).
   * This provides partial endurance credit even if the user hasn't logged benchmark tests.
   * Units:
   * - seconds: total conditioning time in the last 7 days (optional)
   * - meters: total conditioning distance in the last 7 days (optional)
   */
  conditioningSeconds7d?: number;
  conditioningMeters7d?: number;
  pullUpsMax: number;
  pushUpsMax: number;
  dipsMax?: number;

  // Recovery/readiness
  readinessScore: number;             // 0-100
  currentStreak: number;              // days
  workoutsThisWeek: number;           // 0-7

  /**
   * Days since the user's most recent training session. When >=14, strength /
   * endurance / bodyweight pillars get scaled by literature-aligned decay
   * factors so the displayed score reflects detraining honestly instead of
   * showing pre-break 1RM-derived numbers. Recovery and consistency pillars
   * already reflect current state (readiness today, streak now) and aren't
   * decayed further.
   */
  daysSinceLastSession?: number;
}): {
  score: number;
  level: keyof typeof OPERATOR_STANDARDS;
  breakdown: {
    strength: number;       // 0-30 points
    endurance: number;      // 0-25 points
    bodyweight: number;     // 0-20 points
    recovery: number;       // 0-15 points
    consistency: number;    // 0-10 points
  };
  strengths: string[];
  weaknesses: string[];
  recommendation: string;
  /**
   * Detraining decay multipliers actually applied. 1.0 means no decay.
   * Surfaced so callers (and the UI) can explain "score reflects 27 days off."
   */
  detraining?: {
    daysSinceLastSession: number;
    strengthFactor: number;
    enduranceFactor: number;
  };
} {
  const {
    bodyweight, deadlift1RM, squat1RM, bench1RM,
    ruck6MileTime, run1_5MileTime, conditioningSeconds7d, conditioningMeters7d, pullUpsMax, pushUpsMax, dipsMax,
    readinessScore, currentStreak, workoutsThisWeek,
    daysSinceLastSession,
  } = params;

  // STRENGTH SCORE (30 points max)
  const strengthScores = {
    deadlift: Math.min(10, (deadlift1RM / bodyweight / 2.0) * 10),  // 2.0x BW = 10 pts
    squat: Math.min(10, (squat1RM / bodyweight / 1.75) * 10),       // 1.75x BW = 10 pts
    bench: Math.min(10, (bench1RM / bodyweight / 1.5) * 10),        // 1.5x BW = 10 pts
  };
  const strengthScore = strengthScores.deadlift + strengthScores.squat + strengthScores.bench;

  // ENDURANCE SCORE (25 points max)
  let enduranceScore = 0;
  const enduranceScores = { ruck: 0, run: 0 };
  
  if (ruck6MileTime && ruck6MileTime > 0) {
    // 90 min (5400s) = 15 pts, scales down
    enduranceScores.ruck = Math.min(15, Math.max(0, 15 - ((ruck6MileTime - 5400) / 600)));
    enduranceScore += enduranceScores.ruck;
  }
  
  if (run1_5MileTime && run1_5MileTime > 0) {
    // 10 min (600s) = 10 pts, scales down
    enduranceScores.run = Math.min(10, Math.max(0, 10 - ((run1_5MileTime - 600) / 60)));
    enduranceScore += enduranceScores.run;
  }

  // ENDURANCE TRAINING CREDIT (up to 10 points, capped within the 25 endurance points)
  // Rationale: benchmark tests measure capability; training load measures current conditioning work.
  // We award partial credit so HIIT/cardio sessions move endurance even without a formal test logged.
  const TRAINING_MAX = 10;
  const seconds = Math.max(0, conditioningSeconds7d || 0);
  const meters = Math.max(0, conditioningMeters7d || 0);

  // Normalize either by time (target ~90 min/week) or distance (target ~5 km/week equivalent).
  // HIIT sprint sessions often only log distance per interval (no time), so distance normalization matters.
  const timeScore01 = seconds > 0 ? Math.min(1, seconds / (90 * 60)) : 0;
  const distanceScore01 = meters > 0 ? Math.min(1, meters / 5000) : 0;
  const trainingScore01 = Math.max(timeScore01, distanceScore01);
  const enduranceTrainingPoints = TRAINING_MAX * trainingScore01;

  enduranceScore = Math.min(25, enduranceScore + enduranceTrainingPoints);

  // BODYWEIGHT SCORE (20 points max)
  // Pulling endurance: Pull-ups / Chin-ups (max 10)
  // Pushing endurance: best of Push-ups OR Dips (max 10)
  const pullScore = Math.min(10, (pullUpsMax / 20) * 10); // 20 pull-ups = 10 pts
  const pushUpScore = Math.min(10, (pushUpsMax / 80) * 10); // 80 push-ups = 10 pts
  const dipScore = Math.min(10, ((dipsMax || 0) / 30) * 10); // 30 dips = 10 pts
  const pushScore = Math.max(pushUpScore, dipScore);
  const bodyweightScore = pullScore + pushScore;

  // RECOVERY SCORE (15 points max)
  const recoveryScore = (readinessScore / 100) * 15;

  // CONSISTENCY SCORE (10 points max)
  const consistencyScore = Math.min(10,
    (currentStreak * 0.3) +           // Streak: 3 pts max (10 day streak)
    (workoutsThisWeek * 1.0)          // Frequency: 7 pts max (7 workouts)
  );

  // DETRAINING DECAY — applied AFTER raw pillar scoring. 1RM-derived
  // strength + run/ruck-derived endurance + bodyweight-rep capacity all
  // decay during inactivity per Bosquet 2013 and Mujika & Padilla 2000.
  // Recovery (today's readiness) and consistency (current streak) already
  // reflect "now" and aren't decayed twice.
  const sFactor = strengthDecayFactor(daysSinceLastSession ?? 0);
  const eFactor = enduranceDecayFactor(daysSinceLastSession ?? 0);
  const strengthDecayed = strengthScore * sFactor;
  const enduranceDecayed = enduranceScore * eFactor;
  const bodyweightDecayed = bodyweightScore * sFactor; // rep-strength tracks 1RM

  // TOTAL SCORE
  const totalScore = Math.round(
    strengthDecayed +
    enduranceDecayed +
    bodyweightDecayed +
    recoveryScore +
    consistencyScore
  );

  // Determine level
  let level: keyof typeof OPERATOR_STANDARDS = 'beginner';
  if (totalScore >= 85) level = 'elite';
  else if (totalScore >= 70) level = 'advanced';
  else if (totalScore >= 55) level = 'proficient';
  else if (totalScore >= 40) level = 'developing';

  // Identify strengths and weaknesses
  const strengths: string[] = [];
  const weaknesses: string[] = [];

  // Strength analysis
  if (deadlift1RM / bodyweight >= 1.75) strengths.push('Exceptional deadlift strength');
  else if (deadlift1RM / bodyweight < 1.25) weaknesses.push('Deadlift needs work');

  if (squat1RM / bodyweight >= 1.5) strengths.push('Strong squat');
  else if (squat1RM / bodyweight < 1.0) weaknesses.push('Squat strength below standard');

  if (bench1RM / bodyweight >= 1.25) strengths.push('Strong bench press');
  else if (bench1RM / bodyweight < 0.75) weaknesses.push('Upper body strength needs development');

  // Endurance analysis
  if (ruck6MileTime && ruck6MileTime <= 5400) strengths.push('Elite ruck endurance');
  else if (ruck6MileTime && ruck6MileTime > 7200) weaknesses.push('Ruck endurance needs improvement');

  if (run1_5MileTime && run1_5MileTime <= 660) strengths.push('Strong cardiovascular fitness');
  else if (run1_5MileTime && run1_5MileTime > 840) weaknesses.push('Running needs work');

  if ((!ruck6MileTime || ruck6MileTime <= 0) && (!run1_5MileTime || run1_5MileTime <= 0)) {
    if (enduranceTrainingPoints >= 6) strengths.push('Consistent conditioning work (training-based endurance)');
    else if (enduranceTrainingPoints <= 1) weaknesses.push('Endurance benchmarks not logged and low recent conditioning volume');
  }

  // Bodyweight analysis
  if (pullUpsMax >= 15) strengths.push('Excellent pull-up strength');
  else if (pullUpsMax < 8) weaknesses.push('Pull-up strength below standard');

  if (pushUpsMax >= 65 || (dipsMax || 0) >= 25) strengths.push('Strong upper-body endurance (push)');
  else if (pushUpsMax < 35 && (dipsMax || 0) < 15) weaknesses.push('Upper-body endurance (push) needs work');

  // Recovery analysis
  if (readinessScore >= 85) strengths.push('Excellent recovery');
  else if (readinessScore < 60) weaknesses.push('Poor recovery - high fatigue');

  // Consistency analysis
  if (currentStreak >= 10) strengths.push('Exceptional training consistency');
  else if (currentStreak === 0) weaknesses.push('No training consistency');

  // Generate recommendation
  let recommendation = '';
  if (level === 'elite') {
    recommendation = 'Mission ready. Maintain current standards and consider leadership/mentorship roles.';
  } else if (level === 'advanced') {
    recommendation = 'Combat ready. Focus on weak points to achieve elite status.';
  } else if (level === 'proficient') {
    recommendation = 'Mission capable. Consistent training will move you to advanced level.';
  } else if (level === 'developing') {
    recommendation = 'Focus on ' + (weaknesses[0] || 'consistent training') + ' to meet operational standards.';
  } else {
    recommendation = 'Build foundation with consistent training. Focus on ' + (weaknesses[0] || 'all areas') + '.';
  }

  return {
    score: totalScore,
    level,
    breakdown: {
      strength: Math.min(30, Math.round(strengthDecayed)),
      endurance: Math.min(25, Math.round(enduranceDecayed)),
      bodyweight: Math.min(20, Math.round(bodyweightDecayed)),
      recovery: Math.min(15, Math.round(recoveryScore)),
      consistency: Math.min(10, Math.round(consistencyScore)),
    },
    strengths,
    weaknesses,
    recommendation,
    detraining: (daysSinceLastSession ?? 0) >= 14
      ? {
          daysSinceLastSession: daysSinceLastSession!,
          strengthFactor: sFactor,
          enduranceFactor: eFactor,
        }
      : undefined,
  };
}

/**
 * Get specific standard requirements for a level
 */
export function getStandardRequirements(level: keyof typeof OPERATOR_STANDARDS, bodyweight: number = 180) {
  const standard = OPERATOR_STANDARDS[level];
  
  return {
    level,
    label: standard.label,
    description: standard.description,
    requirements: {
      deadlift: Math.round(standard.deadlift * bodyweight),
      squat: Math.round(standard.squat * bodyweight),
      bench: Math.round(standard.bench * bodyweight),
      ruck6Mile: formatTime(standard.ruck6Mile),
      run1_5Mile: formatTime(standard.run1_5Mile),
      pullUps: standard.pullUps,
      pushUps: standard.pushUps,
      dips: standard.dips,
    }
  };
}

/**
 * Format seconds to MM:SS
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Check if user meets minimum operational standards
 */
export function meetsMinimumStandards(params: {
  bodyweight: number;
  deadlift1RM: number;
  squat1RM: number;
  bench1RM: number;
  ruck6MileTime: number | null;
  run1_5MileTime: number | null;
  pullUpsMax: number;
  pushUpsMax: number;
  dipsMax?: number;
}): {
  meets: boolean;
  failing: string[];
} {
  const { bodyweight, deadlift1RM, squat1RM, bench1RM, ruck6MileTime, run1_5MileTime, pullUpsMax, pushUpsMax, dipsMax } = params;
  const proficient = OPERATOR_STANDARDS.proficient;
  
  const failing: string[] = [];
  
  if (deadlift1RM / bodyweight < proficient.deadlift) {
    failing.push(`Deadlift (need ${Math.round(proficient.deadlift * bodyweight)} lbs)`);
  }
  if (squat1RM / bodyweight < proficient.squat) {
    failing.push(`Squat (need ${Math.round(proficient.squat * bodyweight)} lbs)`);
  }
  if (bench1RM / bodyweight < proficient.bench) {
    failing.push(`Bench (need ${Math.round(proficient.bench * bodyweight)} lbs)`);
  }
  if (ruck6MileTime && ruck6MileTime > proficient.ruck6Mile) {
    failing.push(`6-Mile Ruck (need under ${formatTime(proficient.ruck6Mile)})`);
  }
  if (run1_5MileTime && run1_5MileTime > proficient.run1_5Mile) {
    failing.push(`1.5-Mile Run (need under ${formatTime(proficient.run1_5Mile)})`);
  }
  if (pullUpsMax < proficient.pullUps) {
    failing.push(`Pull-Ups (need ${proficient.pullUps})`);
  }
  // Pushing endurance can be satisfied by either push-ups OR dips (strict).
  if (pushUpsMax < proficient.pushUps && (dipsMax || 0) < proficient.dips) {
    failing.push(`Push (need ${proficient.pushUps} push-ups in 2 min OR ${proficient.dips} dips)`);
  }
  
  return {
    meets: failing.length === 0,
    failing
  };
}

